import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Boxes, Globe2, IndianRupee, Wallet, Lock, TrendingUp, UserCheck } from "lucide-react";
import api, { inr } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";

function Stat({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  const tones = {
    blue: "bg-shipany-blue/10 text-shipany-blue",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    navy: "bg-shipany-navy/5 text-shipany-navy",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mono truncate text-xl font-bold text-shipany-navy">{value}</p>
        </div>
      </div>
      {sub && <p className="mt-2 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export default function FranchiseDashboard() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/franchise/dashboard").then(({ data }) => setData(data)).catch(() => setData({}));
    api.get("/franchise/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  if (!data) return <FullScreenLoader label="Loading franchise dashboard" />;
  const e = data.earnings || {};
  const c = data.customers || {};
  const s = data.shipments || {};
  const w = data.wallet || {};

  return (
    <div className="space-y-6" data-testid="franchise-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-shipany-navy">Franchise Dashboard</h1>
          <p className="text-sm text-slate-500">{me?.franchise?.name} · {me?.franchise?.franchise_code}</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${me?.franchise?.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="franchise-status-badge">
          {me?.franchise?.status || "—"}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Users} label="Customers" value={c.total ?? 0} sub={`${c.active ?? 0} active · ${c.kyc_pending ?? 0} KYC pending`} testid="stat-customers" />
        <Stat icon={Boxes} label="Shipments" value={s.total ?? 0} sub={`${s.domestic ?? 0} domestic · ${s.international ?? 0} international`} tone="navy" testid="stat-shipments" />
        <Stat icon={IndianRupee} label="Earned This Month" value={inr(e.this_month ?? 0)} sub={`Total credited ${inr(e.credited ?? 0)}`} tone="green" testid="stat-earnings-month" />
        <Stat icon={Wallet} label="Available Balance" value={inr(w.available ?? 0)} sub={`Locked ${inr(w.locked ?? 0)} · Withdrawn ${inr(w.total_withdrawn ?? 0)}`} tone="amber" testid="stat-wallet" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2" data-testid="earnings-summary-card">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-500"><TrendingUp className="h-4 w-4 text-shipany-blue" /> Commission Summary</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-emerald-50 p-4"><p className="text-xs font-semibold text-emerald-600">Credited</p><p className="mono text-lg font-bold text-emerald-700">{inr(e.credited ?? 0)}</p></div>
            <div className="rounded-xl bg-slate-50 p-4"><p className="text-xs font-semibold text-slate-500">Reversed</p><p className="mono text-lg font-bold text-slate-700">{inr(e.reversed ?? 0)}</p></div>
            <div className="rounded-xl bg-shipany-blue/5 p-4"><p className="text-xs font-semibold text-shipany-blue">Previous Month</p><p className="mono text-lg font-bold text-shipany-navy">{inr(e.previous_month ?? 0)}</p></div>
          </div>
          <p className="mt-4 text-xs text-slate-400">Domestic shipments earn 10% of the base rate. International shipments earn a fixed slab (₹200 / ₹300 / ₹400) once the final billable weight is approved.</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="quick-actions-card">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Quick Actions</h2>
          <div className="mt-4 space-y-2.5">
            <Link to="/franchise/customers" data-testid="quick-add-customer" className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-shipany-navy transition-colors hover:border-shipany-blue hover:bg-shipany-blue/5">
              <UserCheck className="h-4 w-4 text-shipany-blue" /> Onboard a Customer
            </Link>
            <Link to="/franchise/wallet" data-testid="quick-withdraw" className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-shipany-navy transition-colors hover:border-shipany-blue hover:bg-shipany-blue/5">
              <Lock className="h-4 w-4 text-shipany-blue" /> Request Withdrawal
            </Link>
            <Link to="/franchise/earnings" data-testid="quick-earnings" className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-shipany-navy transition-colors hover:border-shipany-blue hover:bg-shipany-blue/5">
              <Globe2 className="h-4 w-4 text-shipany-blue" /> View Earnings Ledger
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
